import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonExtendedDetails } from '@/types/onboarding';

interface PersonDetailsFormProps {
  /**
   * Title for the form section (e.g., "Father", "Mother")
   */
  title: string;

  /**
   * Icon name from Material Symbols (e.g., "man", "woman")
   */
  icon: string;

  /**
   * Initial form data (for pre-population from session)
   */
  initialData?: PersonExtendedDetails;

  /**
   * Callback when form data changes
   */
  onChange: (data: PersonExtendedDetails) => void;

  /**
   * Validation errors for specific fields
   */
  errors?: Partial<Record<keyof PersonExtendedDetails, string>>;

  /**
   * Whether to show validation errors
   */
  showErrors?: boolean;

  /**
   * Whether firstName and lastName are required (default: true)
   * Set to false for grandparents in Step 3 and Step 4
   */
  requireNames?: boolean;
}

/**
 * Reusable Person Details Form Component
 *
 * Collects extended person information with support for:
 * - Required fields: firstName, lastName
 * - Optional fields: birthDate, birthYear, birthPlace
 * - Death information: isDeceased toggle, deathDate, deathYear, deathPlace
 * - Additional notes
 *
 * Features:
 * - Smart date/year switching (full date OR year-only)
 * - "Still living" checkbox that hides/clears death fields
 * - Real-time validation
 * - Accessibility (labels, ARIA attributes)
 * - RTL/LTR support
 * - Production-ready styling matching Stitch design
 *
 * Reusable for:
 * - Step 2: Parents (father, mother)
 * - Step 3: Paternal grandparents
 * - Step 4: Maternal grandparents
 */
export function PersonDetailsForm({
  title,
  icon,
  initialData,
  onChange,
  errors = {},
  showErrors = false,
  requireNames = true,
}: PersonDetailsFormProps) {
  const { t } = useTranslation('onboarding');

  // Form state
  const [formData, setFormData] = useState<PersonExtendedDetails>({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    birthDate: initialData?.birthDate || '',
    birthYear: initialData?.birthYear,
    birthPlace: initialData?.birthPlace || '',
    isDeceased: initialData?.isDeceased || false,
    deathDate: initialData?.deathDate || '',
    deathYear: initialData?.deathYear,
    deathPlace: initialData?.deathPlace || '',
    notes: initialData?.notes || '',
  });

  // Sync with initialData ONLY on initial mount
  useEffect(() => {
    if (initialData) {
      setFormData({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        birthDate: initialData.birthDate || '',
        birthYear: initialData.birthYear,
        birthPlace: initialData.birthPlace || '',
        isDeceased: initialData.isDeceased || false,
        deathDate: initialData.deathDate || '',
        deathYear: initialData.deathYear,
        deathPlace: initialData.deathPlace || '',
        notes: initialData.notes || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to avoid infinite loop

  const handleChange = (field: keyof PersonExtendedDetails, value: string | number | boolean) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };
    setFormData(newFormData);
    onChange(newFormData); // Call onChange immediately with new data
  };

  const handleIsDeceasedChange = (checked: boolean) => {
    let newFormData: PersonExtendedDetails;

    if (!checked) {
      // If "still living" is checked, clear death fields
      newFormData = {
        ...formData,
        isDeceased: false,
        deathDate: '',
        deathYear: undefined,
        deathPlace: '',
      };
    } else {
      newFormData = {
        ...formData,
        isDeceased: true,
      };
    }

    setFormData(newFormData);
    onChange(newFormData); // Call onChange immediately with new data
  };

  return (
    <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 md:p-6 lg:p-8 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 md:mb-6">
        <div className="bg-primary/10 p-1.5 sm:p-2 rounded-md sm:rounded-lg text-primary flex-shrink-0">
          <span className="material-symbols-outlined text-lg sm:text-xl md:text-2xl">{icon}</span>
        </div>
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      </div>

      {/* Form Fields */}
      <div className="space-y-3 sm:space-y-4 md:space-y-5">
        {/* First Name */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor={`${title.toLowerCase()}-firstName`}
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
          >
            <span>
              {t('step2.firstName', 'First Name')}{' '}
              {requireNames && <span className="text-error">*</span>}
            </span>
            {!requireNames && (
              <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
                {t('step2.optional', '(Optional)')}
              </span>
            )}
          </label>
          <input
            id={`${title.toLowerCase()}-firstName`}
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            placeholder={t('step2.firstNamePlaceholder', 'Enter first name')}
            className={`w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base ${
              showErrors && errors.firstName
                ? 'border-error focus:ring-error'
                : 'border-slate-200 dark:border-slate-700'
            }`}
            aria-invalid={showErrors && !!errors.firstName}
            aria-describedby={showErrors && errors.firstName ? `${title.toLowerCase()}-firstName-error` : undefined}
          />
          {showErrors && errors.firstName && (
            <p id={`${title.toLowerCase()}-firstName-error`} className="text-xs sm:text-sm text-error">
              {errors.firstName}
            </p>
          )}
        </div>

        {/* Last Name / Family Name */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor={`${title.toLowerCase()}-lastName`}
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
          >
            <span>
              {t('step2.lastName', 'Family Name')}{' '}
              {requireNames && <span className="text-error">*</span>}
            </span>
            {!requireNames && (
              <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
                {t('step2.optional', '(Optional)')}
              </span>
            )}
          </label>
          <input
            id={`${title.toLowerCase()}-lastName`}
            type="text"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            placeholder={t('step2.lastNamePlaceholder', 'Enter family name')}
            className={`w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base ${
              showErrors && errors.lastName
                ? 'border-error focus:ring-error'
                : 'border-slate-200 dark:border-slate-700'
            }`}
            aria-invalid={showErrors && !!errors.lastName}
            aria-describedby={showErrors && errors.lastName ? `${title.toLowerCase()}-lastName-error` : undefined}
          />
          {showErrors && errors.lastName && (
            <p id={`${title.toLowerCase()}-lastName-error`} className="text-xs sm:text-sm text-error">
              {errors.lastName}
            </p>
          )}
        </div>

        {/* Date of Birth - OPTIONAL */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor={`${title.toLowerCase()}-birthDate`}
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
          >
            <span>{t('step2.birthDate', 'Date of Birth')}</span>
            <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
              {t('step2.optional', '(Optional)')}
            </span>
          </label>
          <input
            id={`${title.toLowerCase()}-birthDate`}
            type="date"
            value={formData.birthDate || ''}
            onChange={(e) => handleChange('birthDate', e.target.value)}
            className="w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base"
          />
        </div>

        {/* Birth Place - OPTIONAL */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor={`${title.toLowerCase()}-birthPlace`}
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
          >
            <span>{t('step2.birthPlace', 'Place of Birth')}</span>
            <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
              {t('step2.optional', '(Optional)')}
            </span>
          </label>
          <input
            id={`${title.toLowerCase()}-birthPlace`}
            type="text"
            value={formData.birthPlace || ''}
            onChange={(e) => handleChange('birthPlace', e.target.value)}
            placeholder={t('step2.birthPlacePlaceholder', 'e.g., Cairo, Egypt')}
            className="w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base"
          />
        </div>

        {/* Still Living Checkbox */}
        <div className="flex items-center gap-2 sm:gap-3 py-1 sm:py-2">
          <input
            id={`${title.toLowerCase()}-stillLiving`}
            type="checkbox"
            checked={!formData.isDeceased}
            onChange={(e) => handleIsDeceasedChange(!e.target.checked)}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary cursor-pointer"
          />
          <label
            htmlFor={`${title.toLowerCase()}-stillLiving`}
            className="text-xs sm:text-sm font-medium cursor-pointer text-slate-900 dark:text-slate-100"
          >
            {t('step2.stillLiving', 'Still living')}
          </label>
        </div>

        {/* Death Date - OPTIONAL, only if deceased */}
        {formData.isDeceased && (
          <>
            <div className="flex flex-col gap-1.5 sm:gap-2">
              <label
                htmlFor={`${title.toLowerCase()}-deathDate`}
                className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
              >
                <span>{t('step2.deathDate', 'Date of Death')}</span>
                <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
                  {t('step2.optional', '(Optional)')}
                </span>
              </label>
              <input
                id={`${title.toLowerCase()}-deathDate`}
                type="date"
                value={formData.deathDate || ''}
                onChange={(e) => handleChange('deathDate', e.target.value)}
                className="w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base"
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:gap-2">
              <label
                htmlFor={`${title.toLowerCase()}-deathPlace`}
                className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
              >
                <span>{t('step2.deathPlace', 'Place of Death')}</span>
                <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
                  {t('step2.optional', '(Optional)')}
                </span>
              </label>
              <input
                id={`${title.toLowerCase()}-deathPlace`}
                type="text"
                value={formData.deathPlace || ''}
                onChange={(e) => handleChange('deathPlace', e.target.value)}
                placeholder={t('step2.deathPlacePlaceholder', 'e.g., Cairo, Egypt')}
                className="w-full h-11 sm:h-12 md:h-14 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 transition-all text-sm sm:text-base"
              />
            </div>
          </>
        )}

        {/* Notes - OPTIONAL */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor={`${title.toLowerCase()}-notes`}
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 flex justify-between"
          >
            <span>{t('step2.notes', 'Notes')}</span>
            <span className="text-[10px] sm:text-xs font-normal text-slate-500 dark:text-slate-400">
              {t('step2.optional', '(Optional)')}
            </span>
          </label>
          <textarea
            id={`${title.toLowerCase()}-notes`}
            value={formData.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder={t('step2.notesPlaceholder', 'Any additional information...')}
            rows={3}
            className="w-full rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 bg-background-light dark:bg-slate-800 focus:ring-2 focus:ring-primary focus:border-primary px-3 sm:px-4 py-2 sm:py-3 transition-all resize-none text-sm sm:text-base"
          />
        </div>
      </div>
    </div>
  );
}
